import React, { useEffect, useState } from 'react';
import MDBox from 'components/MDBox';
import { useForm } from 'react-hook-form';
import Divider from '@mui/material/Divider';
import { useSelector, useDispatch } from 'react-redux';
import SidenavItem from 'examples/Sidenav/SidenavItem';
import { SidenavSwitchBox } from 'components/Ui/styled';
import {
  getSemesterDetails,
  getAttendanceWeeksOptions,
} from 'layouts/applications/attendance/services/attendanceApis';
import {
  sortStudentsAZ,
  getCurrentWeek,
} from 'layouts/studentSuccessAdvocate/ssa/components/ssaCommonUtils';
import { getStudentDetails } from 'layouts/students/service/studentsApi';
import {
  SidebarCheckBoxIcon,
  DataUnavailableText,
  SsaSidebarListWrapper,
  SsaSidebarStudentItem,
} from 'layouts/studentSuccessAdvocate/ssa/components/ssaComponentStyled';
import { FormInputSwitch } from 'components/Common/Forms/FormInputSwitch';
import { handleBooleanProp, showBackdropLoader } from 'utils/commonUtils';
import SsaSidenavCompletedStudentsSection from './SsaSidenavCompletedStudentsSection';
import {
  setNonCompliantOnly,
  setSelectedSsaStudent,
  setSelectedStudentHeadingDetails,
} from 'layouts/studentSuccessAdvocate/ssa/store/ssaStudentDetailSlice/ssaStudentDetailSlice';
import AddStudentToSessionDialog from 'layouts/studentSuccessAdvocate/ssa/components/AddStudentToSessionDialog';
import { setSidebarSelectedStudent } from 'layouts/studentSuccessAdvocate/ssa/store/ssaSidenavBarStudentDetails/ssaSidenavBarStudentDetailsSlice';

const SsaSidenavControls = (props) => {
  const { isSsaMode, darkMode, whiteSidenav, transparentSidenav } = props;

  const dispatch = useDispatch();
  const sidebarStudents =
    useSelector(
      (state) => state.ssaSidenavBarStudentDetails.sideBarSelectedStudent
    ) || [];
  const activeStudentId = useSelector(
    (state) => state.ssaStudentDetail.selectedSsaStudent
  );
  const completedStudents =
    useSelector(
      (state) => state.ssaSidenavBarStudentDetails.completedStudents
    ) || [];

  const [loader, setLoader] = useState('');
  const [currentSemester, setCurrentSemester] = useState('');
  const [openAddStudentDialog, setOpenAddStudentDialog] = useState(false);

  const { control, watch, setValue, reset } = useForm({
    defaultValues: {
      week: '',
      nonCompliantOnly: false,
      includeNotePopups: false,
    },
  });

  useEffect(() => {
    if (isSsaMode) {
      reset({
        nonCompliantOnly: false,
        includeNotePopups: false,
      });
    }
  }, [isSsaMode, reset]);

  const weekWatch = watch('week');
  const nonCompliantOnlywatch = watch('nonCompliantOnly');
  const includeNotePopupswatch = watch('includeNotePopups');

  useEffect(() => {
    dispatch(setNonCompliantOnly(nonCompliantOnlywatch));
  }, [nonCompliantOnlywatch, dispatch]);

  const filteredStudents = sortStudentsAZ(
    nonCompliantOnlywatch
      ? sidebarStudents.filter((s) => !s.inCompliance)
      : sidebarStudents
  );

  const studentList = useSelector(
    (state) => state.ssaStudentDetail.selectedStudentsList || []
  );

  useEffect(() => {
    if (isSsaMode) fetchStudentAttendance();
  }, [isSsaMode]);

  const fetchStudentAttendance = async () => {
    setLoader(true);
    try {
      // --- 1) Get weeks & pick latest ---
      const semesters = await getSemesterDetails();
      setCurrentSemester(semesters[0]);
      const weeks = await getAttendanceWeeksOptions(semesters[0]);

      const allWeeks = weeks?.map((w) => ({
        id: w.descr,
        value: w.descr,
        label: w.descr,
      }));

      const currentWeekObj = getCurrentWeek(weeks);
      const currentWeek = currentWeekObj?.descr || allWeeks.at(-1)?.id || null;

      if (currentWeek) {
        setValue('week', currentWeek);
      }

      // --- 2) Helper to build fallback student object ---
      const fallbackStudent = (student, apiError = true) => ({
        apiError,
        inCompliance: false,
        id: student.studentId,
        semester: currentSemester,
        grade: student.grade || 'N/A',
        lastName: student.lastName || 'Unknown',
        firstName: student.firstName || 'Unknown',
      });

      // --- 3) Fetch each student profile ---
      const getStudentData = async (student) => {
        try {
          const response = await getStudentDetails(student.studentId);
          const item = response?.item;

          if (!item) return fallbackStudent(student);

          const weekNote = item?.attendanceData?.notes?.find(
            (note) => note?.week === currentWeek
          );

          return {
            apiError: false,
            grade: item.grade,
            id: item.studentId,
            lastName: item.lastName,
            semester: currentSemester,
            firstName: item.firstName,
            inCompliance: Boolean(weekNote?.inCompliance),
          };
        } catch (err) {
          console.error(`Student fetch failed: ${student.studentId}`, err);
          return fallbackStudent(student);
        }
      };

      // --- 4) Resolve all students in parallel ---
      const results = await Promise.all(studentList.map(getStudentData));

      const sortedResults = sortStudentsAZ(results);
      dispatch(setSidebarSelectedStudent(sortedResults));

      // if (sortedResults?.length > 0) {
      //   const firstStudent = sortedResults[0];

      //   dispatch(setSelectedSsaStudent(firstStudent?.id));

      //   dispatch(
      //     setSelectedStudentHeadingDetails({
      //       grade: firstStudent?.grade,
      //       studentId: firstStudent?.id,
      //       semester: currentSemester,
      //       lastName: firstStudent?.lastName,
      //       firstName: firstStudent?.firstName,
      //     })
      //   );
      // }

      setLoader(false);
    } catch (error) {
      setLoader(false);
      console.log(
        error?.response?.data?.message || 'Failed to load attendance.'
      );
    }
  };

  if (!isSsaMode) return null;

  const handleSidebarStudentClick = (student) => {
    const id = student?.id || student?.studentId;
    dispatch(setSelectedSsaStudent(id));
    dispatch(
      setSelectedStudentHeadingDetails({
        grade: student?.grade,
        studentId: id,
        semester: currentSemester,
        lastName: student?.lastName,
        firstName: student?.firstName,
      })
    );
  };

  const handleOpenStudentDialog = () => {
    setOpenAddStudentDialog(true);
  };

  const handleCloseStudentDialog = () => {
    setOpenAddStudentDialog(false);
  };

  const dividerLight =
    (!darkMode && !whiteSidenav && !transparentSidenav) ||
    (darkMode && !transparentSidenav && whiteSidenav);

  const hasCompletedStudents = completedStudents?.length >= 1;

  return (
    <>
      {loader ? (
        showBackdropLoader(loader)
      ) : (
        <>
          <Divider light={dividerLight} />

          <MDBox px={2}>
            <MDBox mt={0}>
              <SidenavItem
                name="Add Student to Session"
                onClick={handleOpenStudentDialog}
              />
            </MDBox>

            {/* <SidenavSwitchBox>
              <FormInputSwitch
                sx={{
                  label: { color: 'white', fontWeight: '300' },
                }}
                control={control}
                name="includeNotePopups"
                label="Include note popups"
              />
            </SidenavSwitchBox> */}

            <SidenavSwitchBox>
              <FormInputSwitch
                sx={{
                  label: { color: 'white', fontWeight: '300' },
                }}
                control={control}
                name="nonCompliantOnly"
                label="Non-compliant Only"
              />
            </SidenavSwitchBox>

            {/* === STUDENT DISPLAY BUTTONS === */}
            <SsaSidebarListWrapper>
              {filteredStudents?.map((student, index) => (
                <SsaSidebarStudentItem
                  key={`${student.id}-${student.lastName}-${index}`}
                  isapierror={handleBooleanProp(student.apiError)}
                  onClick={() => handleSidebarStudentClick(student)}
                  isactive={handleBooleanProp(student.id === activeStudentId)}
                >
                  {student.inCompliance && !student.apiError && (
                    <SidebarCheckBoxIcon
                      isactive={handleBooleanProp(
                        student.id === activeStudentId
                      )}
                    />
                  )}
                  {student.lastName}, {student.firstName} ID:{student.id} Grade:
                  {student.grade}
                  {student.apiError && (
                    <DataUnavailableText component="span">
                      (Data unavailable)
                    </DataUnavailableText>
                  )}
                </SsaSidebarStudentItem>
              ))}
            </SsaSidebarListWrapper>

            {/* === COMPLETED STUDENTS SECTION === */}
            <SsaSidenavCompletedStudentsSection
              show={hasCompletedStudents}
              dividerLight={dividerLight}
              activeStudentId={activeStudentId}
              completedStudents={completedStudents}
              onStudentClick={handleSidebarStudentClick}
            />

            <AddStudentToSessionDialog
              open={openAddStudentDialog}
              currentSelectedWeek={weekWatch}
              currentSemester={currentSemester}
              onClose={handleCloseStudentDialog}
            />
          </MDBox>
        </>
      )}
    </>
  );
};

export default SsaSidenavControls;
